/*
   Queueing a review from the intake sheet.
*/

import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query"

import { toSubmittedProfile, type DetectorProfile } from "@/components/domain/detector-profile"
import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"
import type { components } from "@/types/api.gen"

/** The queued review the server answers a 202 with. */
export type CreatedReview = components["schemas"]["ReviewOut"]

export interface CreateReviewInput {
  /** The printed label. The one field the endpoint requires. */
  fpl: File
  /** Masters the reviewer uploaded. Omit to let the server resolve one. */
  masters?: readonly File[]
  /** PPLS URLs the reviewer picked; the server fetches them, a browser cannot. */
  masterUrls?: readonly string[]
  /** EPA registration, as printed. Blank leaves the server to read it off the label. */
  regNr?: string
  /** YYYY-MM-DD, as an `<input type="date">` produces. */
  fplDate?: string
  /** The engagement this review is filed against. Omit for internal work. */
  clientId?: number
  /**
   * Omit to let the server's page threshold decide. `false` runs now at full
   * price, `true` overnight at about half.
   */
  batch?: boolean
  /**
   * The review this one corrects.
   *
   * Files the upload as the next version of that thread rather than a new one,
   * and the server inherits its masters — which is what lets a corrected label
   * be filed with the file alone, no intake wizard and no re-picking a master
   * the reviewer already chose.
   */
  previousReviewId?: number
  /**
   * The detector settings asked for. Only what is set away from Automatic is
   * sent, as a JSON partial; with nothing set the field is left off, which the
   * server reads as Automatic throughout. A corrected version that sends none
   * continues its thread's settings.
   */
  detectorProfile?: DetectorProfile
}

/**
 * POST /reviews/document as multipart, answered 202 with the queued review.
 *
 * `master` and `master_url` are repeated singular field names — one append per
 * item, not arrays. Content-Type stays unset so the browser writes its own
 * multipart boundary; naming it makes the body unparseable server-side
 * (`http` already omits it for a FormData body).
 *
 * Invalidates threads as well as reviews: the queue reads threads, so
 * invalidating reviews alone leaves the list the reviewer returns to showing
 * the state before their own submission.
 */
export function useCreateReview(): UseMutationResult<CreatedReview, Error, CreateReviewInput> {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateReviewInput) => {
      const form = new FormData()
      form.append("fpl", input.fpl)
      for (const master of input.masters ?? []) form.append("master", master)
      for (const url of input.masterUrls ?? []) form.append("master_url", url)
      if (input.regNr) form.append("reg_nr", input.regNr)
      if (input.fplDate) form.append("fpl_date", input.fplDate)
      if (input.clientId !== undefined) form.append("client_id", String(input.clientId))
      if (input.previousReviewId !== undefined) {
        form.append("previous_review_id", String(input.previousReviewId))
      }
      // Only when the reviewer answered: an omitted field is what tells the
      // server to apply its own threshold, so a `false` default would quietly
      // take every review off the overnight lane.
      if (input.batch !== undefined) form.append("batch", String(input.batch))
      const profile = input.detectorProfile ? toSubmittedProfile(input.detectorProfile) : null
      if (profile) form.append("detector_profile", JSON.stringify(profile))
      return http.post<CreatedReview>("/api/v1/reviews/document", form)
    },
    onSuccess: () => {
      /*
         The lists, not every review.

         `qk.reviews.all` is the root of that branch, so invalidating it also
         refetches whichever review the caller happens to be looking at — and a
         caller that creates a review from inside one (filing a corrected file
         against a stopped review) would have its own page refetch and re-render
         underneath the navigation it is about to perform.

         What a new review changes is what the lists contain. The detail query
         of the review that spawned it is not stale.
      */
      void qc.invalidateQueries({ queryKey: qk.threads.all })
      /* The `list` prefix, without a filter: `qk.reviews.list()` carries an
         empty filter object as its third element, which matches only an
         unfiltered list — a queue showing "needs a decision" would not
         refresh. */
      void qc.invalidateQueries({ queryKey: [...qk.reviews.all, "list"] })
    },
  })
}
